'use client';

import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { isEmpty } from 'lodash';
import { AxiosError } from 'axios';
import posthog from 'posthog-js';

import { useDucatClient } from '@/context/DucatClientCtx';
import { useQuantaLogin } from '@/hooks/api/quanta/useQuantaLogin';
import { useQuantaAuth } from '@/hooks/api/quanta/useQuantaAuth';
import { useQuantaCheck } from '@/hooks/api/quanta/useQuantaCheck';
import { useQuantaLevel } from '@/hooks/api/quanta/useQuantaLevel';
import { Referral } from '@/hooks/api/quanta/useQuantaRegister';
import { usePreventMultipleClicks } from '@/hooks/usePreventMultipleClicks';
import Spline from '@splinetool/react-spline';
import { cn } from '@/utils/cn';
import { POSTHOG_EVENTS, POSTHOG_SOURCES } from '@/lib/posthog/sources';
import { captureFailedEvent } from '@/lib/posthog/captureError';

import { useSplineTransition } from './hooks/useSplineTransition';
import ConnectWallet from './components/ConnectWallet';
import InviteCard from './components/InviteCard';
import MissionsCard from './components/MissionsCard';
import StatusCard from './components/StatusCard';
import PointsRankingCard from './components/PointsRankingCard';
import NewLevelUnlockedCard from './components/NewLevelUnlockedCard';
import RevealEvolutionVideoCard from './components/RevealEvolutionVideoCard';
import AnimatedWrapper from './components/AnimatedWrapper';
import LevelUpCard from './components/LevelUpCard';
import InviteFriends from './components/InviteFriends';
import { getMaxPointsForLevel, LEVELS, REFERRAL } from './utils';

const QuantaPage: React.FC = () => {
  const { showSecondSpline, handleFirstSplineLoad } = useSplineTransition();
  const { wallet } = useDucatClient();
  const { signQuanta, registerUser, isRegisterPending, registerError, resetRegisterError } = useQuantaAuth();
  const { isLoading: isCheckLoading, data: checkData, refetch: refetchCheck } = useQuantaCheck();
  const { isButtonBlocked, blockButton, unblockButton } = usePreventMultipleClicks();
  const {
    isLoading: isLoginLoading,
    isError: isLoginError,
    data: loginData,
    refetch: refetchLogin,
  } = useQuantaLogin({ enabled: !!checkData?.data.isRegistered });
  const { mutateAsync: completeLevel } = useQuantaLevel();

  const router = useRouter();
  const searchParams = useSearchParams();
  const referralId = searchParams.get(REFERRAL);

  const [registerData, setRegisterData] = useState<Referral | undefined>();

  const totalPointsResponse = loginData?.data.points_total ?? 0;
  const [totalPoints, setTotalPoints] = useState<number>(totalPointsResponse);

  const levelResponse = loginData?.data.user.level || 1;
  const levelsResponse = loginData?.data.levels ?? null;

  const [level, setLevel] = useState<number>(levelResponse);
  const maxPoints = getMaxPointsForLevel(level, levelsResponse);

  useEffect(() => {
    if (isLoginError) {
      toast.error('Login failed, please try again later.');
    }
  }, [isLoginError]);

  useEffect(() => {
    if (referralId && loginData) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete(REFERRAL);
      router.replace(`/quanta?${params.toString()}`);
    }
  }, [referralId, loginData, router, searchParams]);

  useEffect(() => {
    if (totalPointsResponse && totalPointsResponse !== totalPoints) {
      setTotalPoints(totalPointsResponse);
    }
  }, [totalPoints, totalPointsResponse]);

  useEffect(() => {
    if (levelResponse && levelResponse !== level) {
      if (levelResponse > 3) {
        setLevel(3);
      }

      setLevel(levelResponse);
    }
  }, [level, levelResponse]);

  const [isNextLevelShown, setIsNextLevelShown] = useState<boolean>(false);
  const [isEvolutionVideoShown, setIsEvolutionVideoShown] = useState<boolean>(false);
  const [isLevelUpShown, setIsLevelUpShown] = useState<boolean>(false);

  useEffect(() => {
    if (totalPoints >= maxPoints && !isNextLevelShown && !isLevelUpShown) {
      setIsNextLevelShown(true);
    }
  }, [isNextLevelShown, isLevelUpShown, maxPoints, totalPoints]);

  const startRevealEvolutionVideo = () => {
    setIsEvolutionVideoShown(true);
  };

  const resetStates = () => {
    setIsNextLevelShown(false);
    setIsEvolutionVideoShown(false);
    setIsLevelUpShown(true);
  };

  const handleSecondLevelUp = async () => {
    try {
      const {
        data: { level: newLevel },
      } = await completeLevel();
      await refetchLogin();

      if (newLevel) {
        setLevel(newLevel);
        setIsLevelUpShown(false);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to complete level, please try again later.');
    }
  };

  const renderCard = () => {
    if (isLevelUpShown) {
      const nextLevel = level + 1;

      return (
        <AnimatedWrapper id='level-up-card'>
          <LevelUpCard
            title={LEVELS[nextLevel].title}
            level={nextLevel}
            videoSrc={LEVELS[nextLevel].videoSrc}
            onLevelUp={handleSecondLevelUp}
          />
        </AnimatedWrapper>
      );
    }

    if (isEvolutionVideoShown) {
      return (
        <AnimatedWrapper id='evolution-video-card'>
          <RevealEvolutionVideoCard videoSrc={LEVELS[level].evolutionVideoSrc} onEnded={resetStates} />
        </AnimatedWrapper>
      );
    }

    if (isNextLevelShown) {
      return (
        <AnimatedWrapper id='new-level-card'>
          <NewLevelUnlockedCard videoSrc={LEVELS[level].videoSrc} startRevealEvolutionVideo={startRevealEvolutionVideo} />
        </AnimatedWrapper>
      );
    }

    return (
      <AnimatedWrapper id='status-card'>
        <StatusCard
          id={loginData?.data.user.user_id}
          title={LEVELS[level]?.title}
          level={level}
          videoSrc={LEVELS[level]?.videoSrc}
          totalPoints={totalPoints}
          maxPoints={maxPoints}
        />
      </AnimatedWrapper>
    );
  };

  const login = async () => {
    // prevent multiple clicks
    if (isButtonBlocked) {
      return;
    }

    blockButton(); // block button immediately

    try {
      await signQuanta();

      unblockButton(); // unblock button on success
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      const errorMessage = axiosError.response?.data?.error ?? axiosError.message ?? 'Login failed, please try again later.';

      unblockButton(); // unblock button on error
      toast.error(errorMessage);
    }
  };

  const register = async (referralCode: string) => {
    // prevent multiple clicks
    if (isButtonBlocked) {
      return;
    }

    blockButton(); // block button immediately

    try {
      const headers = await signQuanta();

      if (referralCode) {
        posthog.capture(POSTHOG_EVENTS.REGISTER_REFERRAL_CODE_SENT, {
          referralCode,
          source: POSTHOG_SOURCES.QUANTA_PAGE,
        });
      }

      const registerData = await registerUser(headers, referralCode);

      if (registerData) {
        setRegisterData(registerData.data.referral);

        if (referralCode) {
          posthog.capture(POSTHOG_EVENTS.REGISTER_REFERRAL_CODE_SUCCESS, {
            referralCode,
            registerData,
            source: POSTHOG_SOURCES.QUANTA_PAGE,
          });
        }
      }

      // Note: do not refetch Login manually. Refetching a successful /check will trigger it.
      refetchCheck();
      unblockButton(); // unblock button on success
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      const errorMessage = axiosError.response?.data?.error ?? axiosError.message ?? 'Register failed, please try again later.';

      console.error('login error', errorMessage);
      unblockButton(); // unblock button on error

      // if we want to distinguish logic that signQuanta() failure does not affect on the capture below, we can split the logic above into two try catch blocks
      if (referralCode) {
        captureFailedEvent(error, {
          eventName: POSTHOG_EVENTS.REGISTER_REFERRAL_CODE_FAILED,
          source: POSTHOG_SOURCES.QUANTA_PAGE,
          additionalContext: { referralCode, errorMessage },
        });
      }
    }
  };

  const handleCloseInviteFriends = () => {
    setRegisterData(undefined);
    refetchLogin();
  };

  if (loginData) {
    const {
      referral_id: referralId,
      referred_users: referredUsers,
      referral_points: referralPoints,
      referral: { referred, referrer, points },
      completed_tasks: completedTasks,
      ranking,
    } = loginData.data;

    return (
      <>
        <div className='flex flex-col lg:grid lg:grid-cols-2 3xl:grid-cols-3 gap-4 3xl:gap-[18px] h-full'>
          <div className='flex flex-col lg:col-span-1 gap-4'>
            <PointsRankingCard totalPoints={totalPoints} ranking={ranking} />
            <AnimatePresence mode='wait'>{renderCard()}</AnimatePresence>
          </div>
          <div className='flex flex-col lg:col-span-1 3xl:col-span-2 gap-4 3xl:gap-[18px]'>
            <MissionsCard completedTasks={completedTasks} />
            <div className='hidden 3xl:flex 3xl:flex-1'>
              <InviteCard referralId={referralId} referredUsers={referredUsers} referralPoints={referralPoints} />
            </div>
          </div>
          <div className='flex lg:col-span-2 3xl:hidden'>
            <InviteCard referralId={referralId} referredUsers={referredUsers} referralPoints={referralPoints} />
          </div>
        </div>
        {(!isEmpty(registerData) || !referralId) && (
          <InviteFriends
            referred={registerData?.referred ?? referred}
            referrer={registerData?.referrer ?? referrer}
            points={registerData?.points ?? points}
            close={handleCloseInviteFriends}
          />
        )}
      </>
    );
  }

  return (
    <div className='flex flex-1'>
      <AnimatePresence mode='wait'>
        {!showSecondSpline && (
          <motion.div
            key='first-spline'
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className='absolute inset-x-0 top-1/2 -translate-y-1/2 h-2/3 lg:h-full'
          >
            <Spline scene='https://prod.spline.design/ot56uXSfMFzALj0w/scene.splinecode' onLoad={handleFirstSplineLoad} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence mode='wait'>
        {showSecondSpline && (
          <motion.div
            key='second-spline'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className='absolute inset-x-0 -translate-y-1/3 h-full scale-75 lg:scale-100'
          >
            <Spline scene='https://prod.spline.design/gSG851AloVVBKtAW/scene.splinecode' />
          </motion.div>
        )}
      </AnimatePresence>
      <div className='flex flex-1 h-[calc(100vh-74px)] lg:h-[calc(100vh-94px)] z-20'>
        <AnimatePresence mode='wait'>
          {showSecondSpline && (
            <motion.div
              key='content'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className={cn(
                'relative flex flex-1 justify-center h-fit p-6 z-10',
                !!checkData?.data.isRegistered || !wallet || isCheckLoading
                  ? 'mt-auto lg:mt-[calc(min(536px,100vh-496px))]'
                  : 'mt-auto lg:mt-[calc(min(536px,100vh-596px))]',
              )}
            >
              <ConnectWallet
                isButtonBlocked={isButtonBlocked}
                isWalletConnected={!!wallet}
                isRegistered={!!checkData?.data.isRegistered}
                isCheckLoading={isCheckLoading}
                isLoginLoading={isLoginLoading}
                isRegisterPending={isRegisterPending}
                registerError={registerError}
                login={login}
                register={register}
                onErrorReset={resetRegisterError}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuantaPage;
